import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SwitchCameraOutlinedIcon from '@mui/icons-material/SwitchCameraOutlined';
import { brand, neutral, statusTone } from '../../theme';
import { formatDate } from '../../utils/format';
import { isFinishedStatus } from '../../utils/projectStatus';
import StatusChip from '../ui/StatusChip';
import PriorityFlag from '../Projects/PriorityFlag';
import { parseDeadline, projectStatusDisplay } from '../EditorWorkspace/workflow';
import {
  EditorProject,
  describeDue,
  isAwaitingResponse,
  projectSource,
  taskCounts,
} from './editorProjectModel';

interface EditorProjectRowProps {
  project: EditorProject;
  now: Date;
  /** Draw the divider above this row (every row but the first). */
  divider: boolean;
  /** An action (accept, download) is running on this project. */
  busy: boolean;
  accepting: boolean;
  onAccept: (project: EditorProject) => void;
  onDecline: (project: EditorProject) => void;
  onGenerateTasks: (project: EditorProject) => void;
  onSuggestGuidance: (project: EditorProject) => void;
  onDownloadAssets: (project: EditorProject) => void;
}

/** The whole row opens the project: the title link's hit area covers it. */
const stretchedLinkSx = {
  color: 'text.primary',
  textDecoration: 'none',
  '&::after': { content: '""', position: 'absolute', inset: 0, zIndex: 0 },
  '&:hover': { color: brand.copperDark, textDecoration: 'underline' },
  '&:focus-visible': { outline: 'none' },
  '&:focus-visible::after': { outline: `2px solid ${brand.copper}`, outlineOffset: -2, borderRadius: '8px' },
} as const;

/** Controls sit above the row's link so they stay clickable. */
const aboveLink = { position: 'relative', zIndex: 1 } as const;

const Separator = () => (
  <Box component="span" aria-hidden="true">
    {' · '}
  </Box>
);

/**
 * One project in the editor's list: a link to the workspace (the whole row),
 * its status in the editor's words, who it is from and when it is due, and,
 * while the assignment is unanswered, Accept and Decline right on the row.
 */
const EditorProjectRow: React.FC<EditorProjectRowProps> = ({
  project,
  now,
  divider,
  busy,
  accepting,
  onAccept,
  onDecline,
  onGenerateTasks,
  onSuggestGuidance,
  onDownloadAssets,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const name = project.projectName?.trim() || 'Untitled project';
  const status = projectStatusDisplay(project.status, { viewer: 'editor', hasEditor: Boolean(project.editor) });
  const awaiting = isAwaitingResponse(project);
  // Delivered and cancelled projects are read-only.
  const finished = isFinishedStatus(project.status);
  const tasks = taskCounts(project);
  const source = projectSource(project);
  const multicam = String(project.sourceType || '').toUpperCase() === 'MULTICAM';
  const hasDeadline = Boolean(parseDeadline(project.deadline));
  const due = describeDue(project, now);
  const creator = project.creatorName?.trim();

  const facts: React.ReactNode[] = [];
  if (creator) facts.push(<span key="from">From {creator}</span>);
  if (source) {
    facts.push(
      <Box key="source" component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {source}
      </Box>
    );
  }
  if (hasDeadline) {
    facts.push(
      <span key="due">
        Due {formatDate(project.deadline)}
        {due && (
          <Box
            component="span"
            sx={{ color: due.tone === 'neutral' ? 'inherit' : statusTone[due.tone].fg, fontWeight: due.tone === 'neutral' ? 400 : 600 }}
          >
            {` (${due.text})`}
          </Box>
        )}
      </span>
    );
  }
  if (tasks.total > 0) {
    facts.push(
      <Box key="tasks" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {tasks.done} of {tasks.total} tasks done
      </Box>
    );
  }

  const closeMenu = () => setMenuAnchor(null);
  const runFromMenu = (action: (target: EditorProject) => void) => {
    closeMenu();
    action(project);
  };

  return (
    <Box
      component="li"
      data-testid={`editor-project-${project.id}`}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: { xs: 2, sm: 2.5 },
        py: 2,
        borderTop: divider ? `1px solid ${brand.border}` : 'none',
        transition: 'background-color 120ms ease',
        '&:hover': { backgroundColor: neutral[50] },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.25, rowGap: 0.75, flexWrap: 'wrap' }}>
          <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, minWidth: 0, overflowWrap: 'anywhere' }}>
            <Link component={RouterLink} to={`/edit-projects/${project.id}`} sx={stretchedLinkSx}>
              {name}
            </Link>
          </Typography>
          <StatusChip kind="project" status={project.status} label={status.label} tone={status.tone} />
          {multicam && (
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}
            >
              <SwitchCameraOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              Multicam
            </Box>
          )}
          <PriorityFlag priority={project.priority} />
        </Box>

        {facts.length > 0 && (
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              columnGap: 1,
              rowGap: 0.25,
              color: 'text.secondary',
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            {facts.map((fact, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Separator />}
                {fact}
              </React.Fragment>
            ))}
          </Box>
        )}

        {awaiting && (
          <Box sx={{ ...aboveLink, mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              aria-label={`Accept ${name}`}
              startIcon={accepting ? <CircularProgress size={14} color="inherit" /> : <CheckOutlinedIcon />}
              disabled={busy}
              onClick={() => onAccept(project)}
            >
              {accepting ? 'Accepting…' : 'Accept'}
            </Button>
            <Button size="small" variant="text" color="inherit" aria-label={`Decline ${name}`} disabled={busy} onClick={() => onDecline(project)}>
              Decline
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ ...aboveLink, flexShrink: 0, mt: -0.5, mr: -1 }}>
        <IconButton
          data-testid={`edit-project-actions-${project.id}`}
          aria-label={`More actions for ${name}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          disabled={busy}
        >
          {busy && !accepting ? <CircularProgress size={18} /> : <MoreVertOutlinedIcon />}
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => runFromMenu(onGenerateTasks)} disabled={finished}>
            <ListItemIcon><PlaylistAddOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Generate tasks</ListItemText>
          </MenuItem>
          {/* Suggestions improve existing tasks; with none, the preview used
              to run on an empty list and fail (ED-18). Kept focusable so the
              reason shows. */}
          {!finished && tasks.total === 0 ? (
            <Tooltip title="Generate tasks first" placement="left" describeChild>
              <MenuItem aria-disabled="true" onClick={(event) => event.preventDefault()} sx={{ opacity: 0.45, cursor: 'not-allowed' }}>
                <ListItemIcon><ChecklistOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Suggest task guidance</ListItemText>
              </MenuItem>
            </Tooltip>
          ) : (
            <MenuItem onClick={() => runFromMenu(onSuggestGuidance)} disabled={finished}>
              <ListItemIcon><ChecklistOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Suggest task guidance</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => runFromMenu(onDownloadAssets)}>
            <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download assets</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default EditorProjectRow;
